<template>
  <CrosswordKeyboard v-if="isShowMobileKeyboard && !isPuzzleLocked && !isReadOnly" :clue="selectedClue" :xSelect="xSelect" />
  <section id="content">
     <div class="pause-overlay-container">
        <div class="crossword-content-wrap">
           <div class="container">
              <div>
                 <div class="crossword-puzzle row">
                    <div class="col-12 col-lg-6">
                       <div class="selected-clue-header" v-if="isCreatorMode">
                          <div style="display: contents; font-weight: bold;">
                             {{selectedClue?.number }} {{ xSelect ? "ACROSS" : "DOWN" }}.
                          </div>
                          &nbsp;
                          <div style="display: contents;" v-if="crossword && selectedClue">
                             <input
                                class="clue-input"
                                v-if="xSelect"
                                v-model="crossword.acrossClues.find(clue => clue?.id === selectedClue?.id).clue"
                                placeholder="Enter clue"
                                @keyup.stop
                                >
                             <input
                                class="clue-input"
                                v-else
                                v-model="crossword.downClues.find(clue => clue?.id === selectedClue?.id).clue"
                                placeholder="Enter clue"
                                @keyup.stop
                                >
                          </div>
                       </div>
                       <div class="selected-clue-header" v-else>
                          <div style="display: contents; font-weight: bold;">
                             {{selectedClue?.number }} {{ xSelect ? "ACROSS" : "DOWN" }}.
                          </div>
                          &nbsp;
                          <div style="display: contents;">
                             {{ selectedClue?.clue }}
                          </div>
                       </div>
                       <div class="crossword-grid mx-auto" id="crossword-table">
                          <table ref="crosswordTable">
                             <tbody>
                                <tr v-for="y in crossword.y" :key="y">
                                   <td
                                      v-for="x in crossword.x" :key="x"
                                      class="crossword-grid-cell"
                                      @click="clickCell(x - 1, y - 1)"
                                      :style="cellStyle"
                                      >
                                      <div v-if="isSpecialCellCircle(x, y)" class="special-cell-circle-overlay"></div>
                                      <input
                                         :id="getCellId(x - 1, y - 1)"
                                         v-model="grid[x - 1][y - 1]"
                                         :class="{
                                         'black-cell': isBlackCell(x - 1, y - 1),
                                         'highlighted-cell': !isReadOnly && shouldHighlightCell(x - 1, y - 1),
                                         'selected-cell': !isReadOnly && isSelectedCell(x - 1, y - 1),
                                         'marked-red-cell': isMarkedRedCell(getCellId(x - 1, y - 1)),
                                         'special-cell-highlight': isSpecialCellHighlight(x, y),
                                         'creator-mode-cell': isCreatorMode
                                         }"
                                         :disabled="isPuzzleLocked || isReadOnly"
                                         @focus="handleFocus"
                                         type="text"
                                         inputmode="none"
                                         autocomplete="off"
                                         readonly
                                         />
                                      <span class="clue-number">
                                      {{ getCellNumber(x - 1, y - 1) }}
                                      </span>
                                      <span class="create-match-number-across" v-if="getCreatorDisplayCell(x - 1, y - 1, 'ACROSS')">
                                      <span v-if="clueToNumber.get(getClueForCoords(x - 1, y - 1, 'ACROSS')) > 0 && !isClueComplete(getClueForCoords(x - 1, y - 1, 'ACROSS'), 'ACROSS')" class="creator-good-clue">
                                      {{ clueToNumber.get(getClueForCoords(x - 1, y - 1, 'ACROSS')) }}▷
                                      </span>
                                      <span v-if="clueToNumber.get(getClueForCoords(x - 1, y - 1, 'ACROSS')) === 1 && isClueComplete(getClueForCoords(x - 1, y - 1, 'ACROSS'), 'ACROSS')" class="creator-good-clue">
                                      ✓ ▷
                                      </span>
                                      <span v-if="clueToNumber.get(getClueForCoords(x - 1, y - 1, 'ACROSS')) === 0" class="creator-unknown-clue">
                                      ? ▷
                                      </span>
                                      </span>
                                      <span class="create-match-number-down" v-if="getCreatorDisplayCell(x - 1, y - 1, 'DOWN')">
                                      <span v-if="clueToNumber.get(getClueForCoords(x - 1, y - 1, 'DOWN')) > 0 && !isClueComplete(getClueForCoords(x - 1, y - 1, 'DOWN'), 'DOWN')" class="creator-good-clue">
                                      {{ clueToNumber.get(getClueForCoords(x - 1, y - 1, 'DOWN')) }}▽
                                      </span>
                                      <span v-if="clueToNumber.get(getClueForCoords(x - 1, y - 1, 'DOWN')) === 1 && isClueComplete(getClueForCoords(x - 1, y - 1, 'DOWN'), 'DOWN')" class="creator-good-clue">
                                      ✓ ▽
                                      </span>
                                      <span v-if="clueToNumber.get(getClueForCoords(x - 1, y - 1, 'DOWN')) === 0" class="creator-unknown-clue">
                                      ? ▽
                                      </span>
                                      </span>
                                   </td>
                                </tr>
                                <tr class="lh-sm small mt-2 op-06" >
                                   <td :colspan="crossword.x" style="padding-top: 5px; border: none">
                                      <span v-if="isMobileKeyboardEligible() && !isShowMobileKeyboard && !isPuzzleLocked && !isReadOnly">
                                      Puzzle out of focus.  Click cell to resume.
                                      </span>
                                   </td>
                                </tr>
                             </tbody>
                          </table>
                       </div>
                    </div>
                    <div class="col-12 col-lg-6">
                       <div class="crossword-clues">
                          <div class="crossword-clues-section">
                             <h3 class="p-0 m-0 mb-1">ACROSS</h3>
                             <ul class="crossword-clue-list" id="crossword-clue-list-across">
                                <li
                                   class="crossword-clue"
                                   v-for="clue in crossword.acrossClues"
                                   @click="!isPuzzleLocked && selectClue(clue, true)"
                                   :key="clue.number"
                                   :class="{ 'highlighted-clue': shouldHighlightClue(clue.id) }"
                                   :id="getClueId(clue.x, clue.y, true)"
                                   >
                                   <strong style="user-select: none">{{ clue.number }}. </strong>
                                   <span :style="{ userSelect: isPuzzleLocked ? 'none' : 'text' }">{{ clue.clue }}</span>
                                </li>
                             </ul>
                          </div>
                          <div class="crossword-clues-section">
                             <h3 class="p-0 m-0 mb-1">DOWN</h3>
                             <ul class="crossword-clue-list" id="crossword-clue-list-down">
                                <li
                                   class="crossword-clue"
                                   v-for="clue in crossword.downClues"
                                   @click="!isPuzzleLocked && selectClue(clue, false)"
                                   :key="clue.number"
                                   :class="{ 'highlighted-clue': shouldHighlightClue(clue.id) }"
                                   :id="getClueId(clue.x, clue.y, false)"
                                   >
                                   <strong style="user-select: none">{{ clue.number }}. </strong>
                                   <span :style="{ userSelect: isPuzzleLocked ? 'none' : 'text' }">{{ clue.clue }}</span>
                                </li>
                             </ul>
                          </div>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
        <div class="pause-overlay" v-if="isPuzzleLocked" style="pointer-events: none;">
           <span style="position: absolute; top: 100px;">
           Click play to resume.
           </span>
        </div>
     </div>
     <div class="modal fade" id="autoFillNotificationModal" tabindex="-1" aria-labelledby="autoFillNotificationModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
           <div class="modal-content">
              <div class="modal-header">
                 <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                 <div class="row p-4 bg-white d-flex align-items-center justify-content-center rounded">
                    <div class="col-12 p-4">
                       <h3 class="mb-2">Unable to Autofill Puzzle</h3>
                       <p class="mb-4 fw-normal text-smaller text-black-50">
                          Unable to autofill the puzzle — no viable solutions found using words from our list.
                       </p>
                       <button type="button" class="button button-large m-0 bg-color text-light h-op-08" data-bs-dismiss="modal">Dismiss</button>
                    </div>
                 </div>
              </div>
           </div>
        </div>
     </div>
  </section>
</template>

<script lang="ts">
import { Clue, Crossword, CrosswordSettings } from '@/models/Crossword';
import { Options, Vue } from 'vue-class-component';
import { PropType } from 'vue';
import { createSeededHash } from '@/utils/crypto';
import { getAnonymousUserId, getAnonymousUsername } from '@/utils/usernameTools';
import store, { DataStore, VuexModuleNamespaces } from '@/store';
import CrosswordKeyboard from '@/components/crossword/CrosswordKeyboard.vue';
import { fetchMobilePuzzleKeyboardPreferences } from '@/service/mobile';
import { derivePuzzleSolution, loadSettingsFromLocalStorage } from '@/utils/crossword';
import { matchPattern, checkInWordList, WORD_LIST } from '@/utils/word_list';
import { autofillPuzzle } from '@/utils/autofill_crossword';

// eslint-disable-next-line
const bootstrap = require("bootstrap");
  
@Options({
  components: {
    CrosswordKeyboard
  },
  props: {
      crossword: {
          required: true,
          type: Object as PropType<Crossword>
      },
      isPuzzleLocked: {
        required: false,
        default: false,
        type: Boolean,
      },
      isReadOnly: {
        required: false,
        default: false,
        type: Boolean,
      },
      isCreatorMode: {
        required: false,
        default: false,
        type: Boolean,
      }
  }
})
export default class Puzzle extends Vue {
  
  crossword!: Crossword;
  crosswordSettings: CrosswordSettings = loadSettingsFromLocalStorage();
  isPuzzleLocked!: boolean;
  isReadOnly!: boolean;
  isCreatorMode!: boolean;
  isAutofilling = false;

  grid: string[][] = Array.from({ length: 99 }, () => Array(99).fill(''));
  solutionGrid: string[][] = [];
  selectedCell = [-1, -1]
  xSelect = true;
  
  markedRedCells: Set<string> = new Set<string>();

  clueToNumber: Map<Clue, number> = new Map();

  isTableInFocus = false;
  isShowMobileKeyboard = false;

  // Sizing
  tableSize = 1;

  // Handlers
  private boundResizeHandler!: () => void;
  private boundStartHandler!: () => void;
  private crosswordSettingsHandler!: () => void;
  private clickEventHandler!: () => void;
  private boundKeyupHandler!: (event: KeyboardEvent) => void;
  private boundKeyboardEventHandler!: (event: Event) => void;
  private boundKeydownHandler!: (event: KeyboardEvent) => void;

  public mounted(): void {
    // Bind methods to `this` if needed
    this.boundResizeHandler = this.handleResize.bind(this);

    // Handle resize
    window.addEventListener('resize', this.boundResizeHandler);

    // Handle start
    this.boundStartHandler = async () => {
      this.initGrid();
      if (this.isMobileKeyboardEligible()) {
        this.isShowMobileKeyboard = true;
      }
    };
    window.addEventListener('xframe:startCrossword', this.boundStartHandler, false);

    // Handle crossword settings refresh
    this.crosswordSettingsHandler = async () => {
      this.refreshCrosswordSettings();
    };
    window.addEventListener('xframe:refreshCrosswordSettings', this.crosswordSettingsHandler, false);

    // Listen for key presses
    this.boundKeyupHandler = (event: KeyboardEvent) => {
      // Skip events
      if (this.isPuzzleLocked || this.isReadOnly) {
          return false;
        }
        const x = this.selectedCell[0];
        const y = this.selectedCell[1];
        this.handleKeyDown(event, x, y);
        event.stopPropagation();
      };
      window.addEventListener('keyup', this.boundKeyupHandler);

      // Listen for show comment events
      this.boundKeyboardEventHandler = async (event: Event) => {
        const key = (event as CustomEvent).detail.key as string;
        const x = this.selectedCell[0];
        const y = this.selectedCell[1];
        if (key === "BACKSPACE") {
          this.handleBackspace(x, y);
        } else if (key === "NEXTCLUE") {
          this.jumpToNextUnsolvedClue();
        } else if (key === "CLOSE") {
          this.isShowMobileKeyboard = false;
        } else if (key === "OPEN") {
          this.isShowMobileKeyboard = true;
        } else {
          this.processCellInput(key, x, y);
      }
    };
    window.addEventListener('xframe:crosswordKeyboardEvent', this.boundKeyboardEventHandler, false);

    // Emit keystroke event on click for navigation consistency
    this.clickEventHandler = () => {
      this.emitKeystrokeEvent();
    }
    window.addEventListener('click', this.clickEventHandler); 

    // Prevent Arrow keys from scrolling the page
    this.boundKeydownHandler = (event: KeyboardEvent) => {
      const keysToPrevent = [
        'arrowdown', 'down',
        'arrowup', 'up',
        'tab',
        ' ', 'spacebar', 'space',
        'enter', 'tab', 'return'
      ];

      const target = event.target as HTMLElement;
      let isTyping = false;

      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        if (target.tagName === 'INPUT') {
          const inputElement = target as HTMLInputElement;
          isTyping = !inputElement.id.includes('cell');
        } else {
          isTyping = true;
        }
      }

      if (!isTyping && keysToPrevent.includes(event.key.toLowerCase())) {
        event.preventDefault();
      }
    };
    window.addEventListener('keydown', this.boundKeydownHandler); 

    // Load Crossword info
    this.initGrid();
    this.handleResize();
    this.$forceUpdate();
  }

  public beforeUnmount(): void {
    window.removeEventListener('resize', this.boundResizeHandler);
    window.removeEventListener('xframe:startCrossword', this.boundStartHandler);
    window.removeEventListener('keyup', this.boundKeyupHandler);
    window.removeEventListener('xframe:crosswordKeyboardEvent', this.boundKeyboardEventHandler);
    window.removeEventListener('keydown', this.boundKeydownHandler);
    window.removeEventListener('xframe:refreshCrosswordSettings', this.crosswordSettingsHandler);
    window.removeEventListener('click', this.clickEventHandler);
  }

  public handleResize(): void {
    const crosswordTable = document.getElementById('crossword-table');
    if (crosswordTable) {
      this.tableSize =  crosswordTable.clientWidth;
    }
  }

  public async selectFirstCell(): Promise<void> {
    // Set the cursor to across
    this.xSelect = true;
    
    // Select the first cell
    let cellFound = false;
    let x = 0;
    let y = 0
    for (let j = 0; j < this.crossword.y && !cellFound; j++) {
      for (let i = 0; i < this.crossword.x; i++) {
        if (!this.isBlackCell(i, j)) {
          x = i;
          y = j;
          cellFound = true;
          break; 
        }
      }
    }

    // Set the cell as selected cell
    this.selectedCell = [x, y];
  }

  public async initGrid(): Promise<void> {
    // Init grids
    this.grid = Array.from(Array(this.crossword.y), () =>
      Array(this.crossword.x).fill('')
    );
    this.solutionGrid = Array.from(Array(this.crossword.y), () =>
      Array(this.crossword.x).fill('')
    );

    // Select first cell
    this.selectFirstCell();

    // Derive the solution
    this.derivePuzzleSolution();
  }

  public setGrid(newGrid: string[][] | undefined): void {
    if (newGrid) {
      this.grid = newGrid;
    }
  }

  public emitPuzzleFinishEvent(): void {
    const event = new Event('xframe:finishCrossword', {
      cancelable: true
    });
    window.dispatchEvent(event);
  }

  public emitKeystrokeEvent(): void {
    const event = new Event('xframe:keystrokeEvent', {
      cancelable: true
    });
    window.dispatchEvent(event);
    if (this.isCreatorMode) {
      this.calculatePublishSolutions();
      this.refreshCreatorMatches();
    }
  }

  setSelectedCell(x: number, y: number): void {
    const previouslySelectedCell = this.selectedCell;
    try {
      // If grid is out of bounds, then do nothing
      if (x < 0 || y < 0 || x >= this.crossword.x || y >= this.crossword.y) {
        return;
      }

      // Can't select black cells
      if (this.isBlackCell(x, y)) {
        return;
      }
      // If selecting same cell, then flip direction
      if (this.selectedCell[0] === x && this.selectedCell[1] === y) {
        this.xSelect = !this.xSelect;
      } else {
        // Set selected cell and focus cell
        this.scrollClueIntoView(this.getClueId(x, y, this.xSelect));
        this.selectedCell = [x, y];
      }
    } catch {
      // Ignore bad selections
      this.selectedCell = previouslySelectedCell;
    }
  }
  
  revealGrid(): void {
    this.grid = this.solutionGrid;
    for (let i = 0; i < this.crossword.x; i++) {
      for (let j = 0; j < this.crossword.y; j++) {
        this.removeMarkedRedCell(this.getCellId(i, j));
      }
    }
    
    // Check if puzzle is done
    if (this.isPuzzleSolved) {
      this.emitPuzzleFinishEvent();
    }
  }
  
  shouldHighlightClue(clueId: string): boolean {
    return this.selectedClue?.id === clueId || false;
  }
  
  shouldHighlightCell(x: number, y: number): boolean | undefined {
    const selectedClue = this.selectedClue;
    if (selectedClue) {
      if (this.xSelect) {
        const start = selectedClue.x;
        const end = selectedClue.x + selectedClue.length - 1;
        if (y === selectedClue.y && x >= start && x <= end) {
          return true;
        }
      } else {
        const start = selectedClue.y;
        const end = selectedClue.y + selectedClue.length - 1;
        if (x === selectedClue.x && y >= start && y <= end) {
          return true;
        }
      }
    }
  }
   
  isBlackCell(x: number, y: number): boolean {
    return this.crossword.blackCells.some(cell => cell.x === x && cell.y === y);
  }

  isSelectedCell(x: number, y: number): boolean {
    return this.selectedCell[0] === x && this.selectedCell[1] === y;
  }
  
  getCellNumber(x: number, y: number): number | undefined {
    for (const clue of [...this.crossword.acrossClues, ...this.crossword.downClues]) {
      if (clue.x === x && clue.y === y) {
        return clue.number;
      }
    }
  }

  isClueComplete(clue: Clue, direction: string): boolean {
    if (direction === 'ACROSS') {
      const progress = this.getClueProgress(clue, true);
      return !progress.includes('?');
    } else if (direction === 'DOWN') {
      const progress = this.getClueProgress(clue, false);
      return !progress.includes('?');
    } else {
      return false;
    }
  }

  getCreatorDisplayCell(x: number, y: number, direction: string): boolean {
    // Never display unless in creator mode
    if (!this.isCreatorMode) {
      return false;
    }

    if (direction == 'ACROSS') {
      for (const clue of this.crossword.acrossClues) {
        if (clue.x === x && clue.y === y) {
          return true;
        }
      }
    } else {
      for (const clue of this.crossword.downClues) {
        if (clue.x === x && clue.y === y) {
          return true;
        }
      }
    }
    return false;
  }

  getClueForCoords(x: number, y: number, direction: string): Clue | null {
    try {
      if (direction == 'ACROSS') {
        for (const clue of this.crossword.acrossClues) {
          if (clue.x === x && clue.y === y) {
            return clue;
          }
        }
      } else {
        for (const clue of this.crossword.downClues) {
          if (clue.x === x && clue.y === y) {
            return clue;
          }
        }
      }
      return null;
    } catch(err) {
      return null;
    }
  }
  
  calculatePublishSolutions(): void {
    // Check if clues are complete
    for (const acrossClue of this.crossword.acrossClues) {
      const clueProcess = this.getClueProgress(acrossClue, true);
      acrossClue.solution = createSeededHash(this.crossword.seed, clueProcess);
    }
    for (const downClue of this.crossword.downClues) {
      const clueProcess = this.getClueProgress(downClue, false);
      downClue.solution = createSeededHash(this.crossword.seed, clueProcess);
    }

    // Refresh solution grid
    this.derivePuzzleSolution()
  }

  checkIsPublishReady(): boolean {
    try {
      // Check if clues are complete
      for (const acrossClue of this.crossword.acrossClues) {
        const clueProcess = this.getClueProgress(acrossClue, true);
        if (!(!clueProcess.includes("?") && acrossClue.clue)) {
          return false;
        }
      }
      for (const downClue of this.crossword.downClues) {
        const clueProcess = this.getClueProgress(downClue, false);
        if (!(!clueProcess.includes("?") && downClue.clue)) {
          return false;
        }
      }
      // Calculate solutions
      this.calculatePublishSolutions();
      
      // If all clues are complete then it's ready
      return true
    } catch(err) {
      return false;
    }
  }

  checkCluesInWordlist(): string[] {
    try {
      const results: string[] = [];
      for (const acrossClue of this.crossword.acrossClues) {
        const clueProcess = this.getClueProgress(acrossClue, true);
        if (!checkInWordList(clueProcess)) {
          results.push(clueProcess);
        }
      }
      for (const downClue of this.crossword.downClues) {
        const clueProcess = this.getClueProgress(downClue, false);
        if (!checkInWordList(clueProcess)) {
          results.push(clueProcess);
        }
      }
      return results;
    } catch(err) {
      return [];
    }
  }

  refreshCreatorMatches(): void {
    // Skip if not in creator mode
    if (!this.isCreatorMode) {
      return;
    }

    // Clean slate
    this.clueToNumber = new Map();

    try {
      for (const acrossClue of this.crossword.acrossClues) {
        const clueProcess = this.getClueProgress(acrossClue, true);
        if (clueProcess && !/^\?+$/.test(clueProcess)) {
          this.clueToNumber.set(acrossClue, matchPattern(clueProcess).length);
        }
      }
      for (const downClue of this.crossword.downClues) {
        const clueProcess = this.getClueProgress(downClue, false);
        if (clueProcess && !/^\?+$/.test(clueProcess)) {
          this.clueToNumber.set(downClue, matchPattern(clueProcess).length);
        }
      }
    } catch(err) {
      // Ignore
    }
  }
  
  scrollClueIntoView(clueId: string): void {
      const element: HTMLElement | null = document.getElementById(clueId);
      const div: HTMLElement | null = document.getElementById(this.xSelect ? "crossword-clue-list-across" : "crossword-clue-list-down");
      if (div && element) {
        div.scrollTo({
            top: element.offsetTop - 50, 
            behavior: 'auto'
        });
      }
  }

  clickCell(x: number, y: number): void {
    // No action allowed if disabled
    if (this.isPuzzleLocked) {
      return;
    }
    this.setSelectedCell(x, y);
  }

  handleBackspace(x: number, y: number): void {
    this.removeMarkedRedCell(this.getCellId(x, y));
    const currentValue = this.grid[x][y];
    if (currentValue) {
      this.clearInput(x, y);
    }
    if (this.xSelect) {
      this.setSelectedCell(x - 1, y);
    } else {
      this.setSelectedCell(x, y - 1);
    }
  }

  handleFocus(): void {
    if (this.isMobileKeyboardEligible()) {
      this.isShowMobileKeyboard = true;
    }
  }
  
  handleKeyDown(event: KeyboardEvent, x: number, y: number): void {
    // No action allowed if disabled
    if (this.isPuzzleLocked) {
      return;
    }

    // Process cell input
    const key = event.key.toLowerCase();

    if (key === 'backspace') {
      this.handleBackspace(x, y);
    } else if (['enter', 'tab', 'return'].includes(key)) {
      this.jumpToNextUnsolvedClue();
    } else if ([' ', 'spacebar', 'space'].includes(key)) {
      if (this.crosswordSettings.spaceToggleDirection) {
        this.xSelect = !this.xSelect;
      } else {
        this.jumpToNextUnsolvedClue();
      }
    } else if (['arrowup', 'up'].includes(key)) {
      if (this.xSelect) {
        this.xSelect = !this.xSelect;
      } else {
        this.setSelectedCell(x, y - 1);
      }
    } else if (['arrowdown', 'down'].includes(key)) {
      if (this.xSelect) {
        this.xSelect = !this.xSelect;
      } else {
        this.setSelectedCell(x, y + 1);
      }
    } else if (['arrowleft', 'left'].includes(key)) {
      if (!this.xSelect) {
        this.xSelect = !this.xSelect;
      } else {
        this.setSelectedCell(x - 1, y);
      }
    } else if (['arrowright', 'right'].includes(key)) {
      if (!this.xSelect) {
        this.xSelect = !this.xSelect;
      } else {
        this.setSelectedCell(x + 1, y);
      }
    } else if (this.isLetter(event.key)) {
      this.processCellInput(event.key, x, y);
    }

    // Emit keystroke event
    this.emitKeystrokeEvent();
  }
  
  jumpToNextUnsolvedRowClue(currentEnd: number, currentRow: number): boolean {
    const gridWidth = this.crossword.x;
    const gridLength = this.crossword.y;

    for (let colIndex = currentRow; colIndex < gridLength; colIndex++) {
      const rowStart = currentRow == colIndex ? currentEnd + 1 : 0;
      for (let rowIndex = rowStart; rowIndex < gridWidth; rowIndex++) {
        let cell = this.grid[rowIndex][colIndex];
        if (this.crosswordSettings.skipFilledLetters) {
          if (!this.isBlackCell(rowIndex, colIndex) && !cell) {
            this.setSelectedCell(rowIndex, colIndex);
            this.xSelect = true;
            return true;
          }
        } else {
          if (!this.isBlackCell(rowIndex, colIndex)) {
            this.setSelectedCell(rowIndex, colIndex);
            this.xSelect = true;
            return true;
          }
        }
      }
    }
    return false;
  }
  
  jumpToNextUnsolvedColumnClue(currentStart: number, currentCol: number): boolean {
    const gridWidth = this.crossword.x;
    const gridLength = this.crossword.y;

    for (let colIndex = currentStart; colIndex < gridLength; colIndex++) {
      const rowStart = currentStart == colIndex ? currentCol + 1 : 0;
      const rowClues = this.crossword.downClues.filter(clue => clue.y === colIndex);
      for (let rowIndex = rowStart; rowIndex < gridWidth; rowIndex++) {
        for (const clue of rowClues) {
          const column = clue.x;
          const clueStart = clue.y;
          const clueEnd = clue.y + clue.length - 1;
          // Check if cell is part of this clue
          if (column == rowIndex && colIndex >= clueStart && colIndex <= clueEnd && !this.isBlackCell(rowIndex, colIndex)) {
            // Skip to the first blank cell in the clue
            for (let i = clueStart; i <= clueEnd; i++) {
              let cell = this.grid[column][i];
              if (this.crosswordSettings.skipFilledLetters) {
                if (!cell) {
                  this.setSelectedCell(column, i);
                  this.xSelect = false;
                  return true;
                }
              } else {
                this.setSelectedCell(column, i);
                this.xSelect = false;
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  }

  jumpToNextUnsolvedClue(): void {
    let isNewClueFound = false;
    if (this.xSelect && this.selectedClue) {
      isNewClueFound = this.jumpToNextUnsolvedRowClue(
        this.selectedClue.x + this.selectedClue.length - 1,
        this.selectedClue.y
      );
      if (!isNewClueFound) {
        this.jumpToNextUnsolvedColumnClue(-1, 0);
      }
    } else if (this.selectedClue) {
      isNewClueFound = this.jumpToNextUnsolvedColumnClue(
        this.selectedClue.y,
        this.selectedClue.x
      );
      if (!isNewClueFound) {
        this.jumpToNextUnsolvedRowClue(-1, 0);
      }
    }
  }

  jumpToNextClueCell(): void {
    const x = this.selectedCell[0];
    const y = this.selectedCell[1];
    if (this.selectedClue) {
      if (this.xSelect) {
        // Get selected clue specs
        const clueEnd = this.selectedClue.x + this.selectedClue.length - 1;
        const clueRow = this.selectedClue.y;

        // No action if at clue end
        if (x == clueEnd) {
          return;
        }

        // Jump to next cell
        this.setSelectedCell(x + 1, clueRow);
      } else {
        // Get selected clue specs
        const clueEnd = this.selectedClue.y + this.selectedClue.length - 1;
        const clueCol = this.selectedClue.x;

        // No action if at clue end
        if (y == clueEnd) {
          return;
        }

        // Jump to next cell
        this.setSelectedCell(clueCol, y + 1);
      }
    }
  }
  
  jumpToNextUnsolvedCell(): void {
    const x = this.selectedCell[0];
    const y = this.selectedCell[1];
    if (this.selectedClue) {
      if (this.xSelect) {
        this.jumpToNextUnsolvedRowClue(x, this.selectedClue.y);
      } else {
        // Get selected clue specs
        const clueEnd = this.selectedClue.y + this.selectedClue.length - 1;
        const clueCol = this.selectedClue.x;
        // Jump to next cell if reached the end of the clue
        if (y == clueEnd) {
          this.jumpToNextUnsolvedClue();
          return;
        }
        // Check rest of word
        for (let i = y; i <= clueEnd; i++) {
          if (!this.grid[clueCol][i]) {
            this.setSelectedCell(clueCol, i);
            return;
          }
        }
        // Default action is to jump to next clue
        this.jumpToNextUnsolvedClue();
        return;
      }
    }
  }
  
  processCellInput(newChar: string, x: number, y: number): void {
    const inputElement = document.getElementById(this.getCellId(x, y)) as HTMLInputElement;
    const currentValue = this.grid[x][y];
    if (newChar && this.isLetter(newChar)) {
      this.clearInput(x, y);
      inputElement.value = newChar.toUpperCase();
      this.grid[x][y] = newChar.toUpperCase();

      // Take no action if at the end of a clue and setting is enabled
      let isAtEndClue = false;
      const selectedClue = this.selectedClue;
      if (selectedClue && !this.xSelect) {
        const clueEnd = this.selectedClue.y + this.selectedClue.length - 1;
        if (y == clueEnd) {
          isAtEndClue = true;
        }
      } else if (selectedClue) {
        const clueEnd = this.selectedClue.x + this.selectedClue.length - 1;
        if (x == clueEnd) {
          isAtEndClue = true;
        }
      }

      // Go to next cell
      if (!isAtEndClue && !this.crosswordSettings.skipFilledLetters) {
        this.jumpToNextClueCell()
      } else if (!(isAtEndClue && !this.crosswordSettings.moveToNextClueAtEndOfWord)) {
        this.jumpToNextUnsolvedCell();
      }
    } else if (newChar && !this.isLetter(newChar)) {
      if (currentValue && this.isLetter(currentValue[0])) {
        inputElement.value = this.grid[x][y][0] ? this.grid[x][y][0] : '';
        this.grid[x][y] = this.grid[x][y][0] ? this.grid[x][y][0] : '';
      } else if (currentValue && !this.isLetter(currentValue[0])) {
        this.clearInput(x, y);
      }
    }

    // Remove the marked cell
    this.removeMarkedRedCell(this.getCellId(x, y));

    // Check if puzzle is done
    if (this.isPuzzleSolved) {
      this.emitPuzzleFinishEvent();
    }

    // Emit keystroke event
    this.emitKeystrokeEvent();
  }
  
  clearInput(x: number, y: number): void {
    const inputElement = document.getElementById(this.getCellId(x, y)) as HTMLInputElement;
    inputElement.value = '';
    this.grid[x][y] = '';
  }

  getCellId(x: number, y: number): string {
    return "cell-" + x + "-" + y;
  }

  getClueId(x: number, y: number, xSelect: boolean): string {
    return "clue-" + x + "-" + y + "-" + (xSelect ? "across" : "down");
  }

  isLetter(str: string): boolean {
    return /^[a-zA-Z]$/.test(str);
  }
  
  selectClue(clue: Clue, xSelect: boolean): void {
    this.xSelect = xSelect;
    this.selectedCell = [clue.x, clue.y];
  }
  
  derivePuzzleSolution(): void {
    this.solutionGrid =  derivePuzzleSolution(this.crossword);
  }
  
  checkPuzzle(): void {
    for (let i = 0; i < this.crossword.x; i++) {
      for (let j = 0; j < this.crossword.y; j++) {
        const cellValue = this.grid[i][j];
        const solutionValue = this.solutionGrid[i][j];
        if (cellValue && cellValue != solutionValue) {
          this.addMarkedRedCell(this.getCellId(i, j));
        }
      }
    }
  }

  checkSelectedCell(): void {
    const x = this.selectedCell[0];
    const y = this.selectedCell[1];
    for (let i = 0; i < this.crossword.x; i++) {
      for (let j = 0; j < this.crossword.y; j++) {
        const cellValue = this.grid[i][j];
        const solutionValue = this.solutionGrid[i][j];
        if (cellValue && cellValue != solutionValue && x === i && y === j) {
          this.addMarkedRedCell(this.getCellId(i, j));
        }
      }
    }
  }

  revealSelectedCell(): void {
    const x = this.selectedCell[0];
    const y = this.selectedCell[1];
    for (let i = 0; i < this.crossword.x; i++) {
      for (let j = 0; j < this.crossword.y; j++) {
        const solutionValue = this.solutionGrid[i][j];
        if (x === i && y == j) {
          this.grid[x][y] = solutionValue;
          this.removeMarkedRedCell(this.getCellId(i, j));
        }
      }
    }

    // Check if puzzle is done
    if (this.isPuzzleSolved) {
      this.emitPuzzleFinishEvent();
    }
  }
  
  addMarkedRedCell(cellId: string): void {
    this.markedRedCells.add(cellId);
  }

  removeMarkedRedCell(cellId: string): void {
    this.markedRedCells.delete(cellId);
  }

  isMarkedRedCell(cellId: string): boolean {
    return this.markedRedCells.has(cellId);
  }

  isSpecialCellHighlight(x: number, y: number): boolean {
    if (!this.crossword.specialCells) {
      return false;
    }

    return this.crossword.specialCells.some(cell => 
      cell.x === x && cell.y === y && cell.highlight
    );
  }

  isSpecialCellCircle(x: number, y: number): boolean {
    if (!this.crossword.specialCells) {
      return false;
    }

    return this.crossword.specialCells.some(cell => 
      cell.x === x && cell.y === y && cell.circle
    );
  }

  resetPuzzle(): void {
    this.grid = Array.from(Array(this.crossword.y), () =>
      Array(this.crossword.x).fill('')
    );
    this.selectFirstCell();
  }

  resetClues(): void {
    for (const acrossClue of this.crossword.acrossClues) {
      acrossClue.clue = "";
    }
    for (const downClue of this.crossword.downClues) {
      downClue.clue = "";
    }
  }

  checkTableInFocus(): boolean {
    const x = this.selectedCell[0];
    const y = this.selectedCell[1];
    const inputElement = document.getElementById(this.getCellId(x, y));
    const activeElement = document.activeElement;
    if (inputElement === document.activeElement || activeElement?.id.includes('cell')) {
      return true;
    } else {
      return false;
    }
  }

  isMobileKeyboardEligible(): boolean {
    if (fetchMobilePuzzleKeyboardPreferences()) {
      return true;
    } else {
      return false;
    }
  }

  refreshCrosswordSettings(): void {
    this.crosswordSettings = loadSettingsFromLocalStorage();
  }

  getClueProgress(clue: Clue | null, xSelect: boolean): string {
    if (clue && this.grid) {
      if (xSelect) {
        let enteredValue = "";
        for (let i = clue.x; i < clue.x + clue.length; i++) [
          enteredValue += this.grid[i][clue.y] ? this.grid[i][clue.y] : "?"
        ]
        return enteredValue;
      } else {
        let enteredValue = "";
        for (let i = clue.y; i < clue.y + clue.length; i++) [
          enteredValue += this.grid[clue.x][i] ? this.grid[clue.x][i] : "?"
        ]
        return enteredValue;
      }
    } else {
      return "";
    }
  }

  autofillPuzzle(): void {
    const currentGrid = this.grid.map(row => [...row]);
    const newGrid = autofillPuzzle(currentGrid, this.crossword, WORD_LIST);
    if (newGrid) {
      this.grid = newGrid;
    } else {
      this.showUnableToSolveModal();
    }
  }

  showUnableToSolveModal(): void {
    const modal = new bootstrap.Modal(document.getElementById('autoFillNotificationModal'), {});
    modal.show();
  }
  
  get username(): string {
      const username = store.getters[`${VuexModuleNamespaces.App}/${DataStore.getters.getUsername.name}`];
      if (username) {
          return username;
      } else {
          return getAnonymousUsername();
      }
  }

  get userId(): string {
      const username = store.getters[`${VuexModuleNamespaces.App}/${DataStore.getters.getUserId.name}`];
      if (username) {
          return username;
      } else {
          return getAnonymousUserId();
      }
  }

  get isPuzzleSolved(): boolean {
    return JSON.stringify(this.grid) === JSON.stringify(this.solutionGrid);
  }

  get cellStyle(): Record<string, string> {
    const cells = this.crossword.y ? this.crossword.y : 1;
    const cellSize = Math.min(this.tableSize / cells, 50);
    return {
        width: `${cellSize}px`,
        height: `${cellSize}px`,
        maxWidth: `${cellSize}px`,
        maxHeight: `${cellSize}px`,
        minWidth: `${cellSize}px`,
        minHeight: `${cellSize}px`,
        fontSize: `${cellSize * .45}px`,
      };
  }
  
  get isSolved(): boolean {
    // Check if both arrays are empty
    if (this.solutionGrid.length === 0 && this.grid.length === 0) return false;
    // Check if the arrays have different lengths
    if (this.solutionGrid.length !== this.grid.length) return false;
    for (let i = 0; i < this.solutionGrid.length; i++) {
      if (this.solutionGrid[i].length !== this.grid[i].length) return false;
      for (let j = 0; j < this.solutionGrid[i].length; j++) {
        // Check if both arrays only contain empty strings
        if (this.solutionGrid.every(row => row.every(val => val === "")) && this.grid.every(row => row.every(val => val === ""))) {
          return false;
        }
        if (this.solutionGrid[i][j] !== this.grid[i][j]) return false;
      }
    }
    return true;
  }
  
  get selectedClue(): Clue | null {
    const { acrossClues, downClues } = this.crossword;
    const [x, y] = this.selectedCell;
    if (this.xSelect) {
      for (const clue of acrossClues) {
        const start = clue.x;
        const end = clue.x + clue.length - 1;
        if (y === clue.y && x >= start && x <= end) {
          return clue
        }
      }
    } else {
      for (const clue of downClues) {
        const start = clue.y;
        const end = clue.y + clue.length - 1;
        if (x === clue.x && y >= start && y <= end) {
          return clue
        }
      }
    }
    return null;
  }
  
}
</script>
  
<style scoped>
.clue-input {
  background: none;
  border: none;
  width: 50%;
}
</style>
